import { useEffect, useMemo, useState } from 'react';
import { auditTrailApi, type PagedAuditTrailResponse } from '@/services/auditTrailApi';
import { getApiErrorStatus } from '@/services/reportPaging';
import type { AuditTrailQuery } from '@/utils/AuditTrail/types';
import { mapAuditTrailRow } from '@/utils/AuditTrail/mappers';

interface UseAuditTrailDataParams {
  canView: boolean;
  query: AuditTrailQuery;
  page: number;
  pageSize: number;
  companyId: string | undefined;
}

export type AuditTrailSummary = Pick<
  PagedAuditTrailResponse,
  'totalTimesheetsCount' | 'submittedCount' | 'approvedCount' | 'rejectedCount' | 'recalledCount' | 'draftCount'
>;

export function useAuditTrailData({ canView, query, page, pageSize, companyId }: UseAuditTrailDataParams) {
  const [result, setResult] = useState<PagedAuditTrailResponse | null>(null);
  const [summary, setSummary] = useState<AuditTrailSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryAccessDenied, setSummaryAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    setSummary(null);
    setSummaryError(null);
    setSummaryAccessDenied(false);
    if (!canView) return;

    let cancelled = false;
    auditTrailApi
      .getAuditTrail({ pageNumber: 1, pageSize: 1 })
      .then(({ totalTimesheetsCount, submittedCount, approvedCount, rejectedCount, recalledCount, draftCount }) => {
        if (!cancelled) {
          setSummary({ totalTimesheetsCount, submittedCount, approvedCount, rejectedCount, recalledCount, draftCount });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (getApiErrorStatus(err) === 403) setSummaryAccessDenied(true);
        else setSummaryError(err instanceof Error
          ? `Failed to load the audit trail summary: ${err.message}`
          : 'Failed to load the audit trail summary');
      });
    return () => { cancelled = true; };
  }, [canView, companyId]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    auditTrailApi
      .getAuditTrail({ ...query, pageNumber: page, pageSize })
      .then((response) => {
        if (cancelled) return;
        setResult(response);
        setAccessDenied(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (getApiErrorStatus(err) === 403) setAccessDenied(true);
        else setLoadError(err instanceof Error ? err.message : 'Failed to load the audit trail');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [canView, query, page, pageSize, companyId]);

  const rows = useMemo(() => (result?.items ?? []).map(mapAuditTrailRow), [result]);

  return {
    result, summary, summaryError, rows, isLoading, loadError,
    accessDenied: accessDenied || summaryAccessDenied,
  };
}
